import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import type { RightPanelTab } from '../pages/PlanningPageHelpers';
import { isIOSApp } from '../utils/platformDetection';

export const ARTIFACT_DISPLAY_TABS = [
  'requirements',
  'user-flow',
  'design',
  'architecture',
  'tasks',
  'env',
  'payments',
  'preview',
  'team',
  'organization',
] as const satisfies readonly RightPanelTab[];

export type ArtifactDisplayTab = typeof ARTIFACT_DISPLAY_TABS[number];
/**
 * `timeline`/`sidebar`: an explicit user action — always focuses the tab.
 * `generated`: an artifact appeared — updates an existing category without
 *   stealing focus, and may add a category only while the workspace is open.
 * `stream`: live generation started — opens the workspace and activates the
 *   artifact being produced. Closing it still stops following for that run.
 */
export type ArtifactDisplaySource = 'sidebar' | 'timeline' | 'generated' | 'stream';

export interface ArtifactDisplayDescriptor {
  tab: ArtifactDisplayTab;
  itemId: string | null;
  subview: string | null;
}

export interface OpenArtifactDisplayRequest {
  tab: ArtifactDisplayTab;
  source: ArtifactDisplaySource;
  itemId?: string | null;
  subview?: string | null;
}

export interface ArtifactSelectionUpdate {
  itemId?: string | null;
  subview?: string | null;
}

export interface ArtifactDisplayState {
  tabs: ArtifactDisplayDescriptor[];
  activeTab: ArtifactDisplayTab | null;
  isOpen: boolean;
}

export interface ArtifactDisplayContextValue {
  state: ArtifactDisplayState;
  openArtifact: (request: OpenArtifactDisplayRequest) => void;
  activateTab: (tab: ArtifactDisplayTab) => void;
  closeTab: (tab: ArtifactDisplayTab) => void;
  closePanel: () => void;
  updateSelection: (tab: ArtifactDisplayTab, update: ArtifactSelectionUpdate) => void;
}

interface ArtifactDisplayProviderProps {
  projectId: string | null;
  children: React.ReactNode;
}

interface StoredArtifactDisplayState extends ArtifactDisplayState {
  version: typeof STORAGE_VERSION;
}

type DisplayAction =
  | { type: 'open'; request: OpenArtifactDisplayRequest }
  | { type: 'activate'; tab: ArtifactDisplayTab }
  | { type: 'close-tab'; tab: ArtifactDisplayTab }
  | { type: 'close-panel' }
  | { type: 'update-selection'; tab: ArtifactDisplayTab; update: ArtifactSelectionUpdate };

interface ProjectDisplayState {
  projectId: string | null;
  display: ArtifactDisplayState;
}

type ProjectAction =
  | DisplayAction
  | { type: 'load-project'; projectId: string | null; display: ArtifactDisplayState };

interface ArtifactDisplayEvent extends Event {
  detail?: unknown;
}

const STORAGE_VERSION = 1 as const;
const STORAGE_PREFIX = `artifact-display:v${STORAGE_VERSION}`;
const TAB_SET: ReadonlySet<string> = new Set(ARTIFACT_DISPLAY_TABS);
const SOURCE_SET: ReadonlySet<string> = new Set(['sidebar', 'timeline', 'generated', 'stream']);
const EMPTY_DISPLAY_STATE: ArtifactDisplayState = { tabs: [], activeTab: null, isOpen: false };

export const OPEN_ARTIFACT_DISPLAY_EVENT = 'open_artifact_display';
/** Dispatched when the user closes the panel (`detail: { tab: null }`) or a tab (`detail: { tab }`). */
export const ARTIFACT_DISPLAY_DISMISSED_EVENT = 'artifact_display_dismissed';

const announceDismissed = (tab: ArtifactDisplayTab | null): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new window.CustomEvent(ARTIFACT_DISPLAY_DISMISSED_EVENT, { detail: { tab } }));
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

export const isArtifactDisplayTab = (value: unknown): value is ArtifactDisplayTab => (
  typeof value === 'string' && TAB_SET.has(value)
);

const isArtifactDisplaySource = (value: unknown): value is ArtifactDisplaySource => (
  typeof value === 'string' && SOURCE_SET.has(value)
);

const isNullableString = (value: unknown): value is string | null | undefined => (
  value === undefined || value === null || typeof value === 'string'
);

const normalizeText = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();

  return trimmed ? trimmed : null;
};

const normalizeDescriptor = (value: unknown): ArtifactDisplayDescriptor | null => {
  if (!isRecord(value) || !isArtifactDisplayTab(value.tab)) return null;
  if (['design', 'env', 'preview', 'payments'].includes(value.tab) && isIOSApp()) return null;
  if (!isNullableString(value.itemId) || !isNullableString(value.subview)) return null;

  return {
    tab: value.tab,
    itemId: normalizeText(value.itemId),
    subview: normalizeText(value.subview),
  };
};

const normalizeOpenRequest = (value: unknown): OpenArtifactDisplayRequest | null => {
  if (!isRecord(value) || !isArtifactDisplayTab(value.tab)) return null;
  if (['design', 'env', 'preview', 'payments'].includes(value.tab) && isIOSApp()) return null;
  if (!isArtifactDisplaySource(value.source)) return null;
  if (!isNullableString(value.itemId) || !isNullableString(value.subview)) return null;
  const itemId = Object.prototype.hasOwnProperty.call(value, 'itemId')
    ? { itemId: normalizeText(value.itemId as string | null | undefined) }
    : {};
  const subview = Object.prototype.hasOwnProperty.call(value, 'subview')
    ? { subview: normalizeText(value.subview as string | null | undefined) }
    : {};

  return {
    tab: value.tab,
    source: value.source,
    ...itemId,
    ...subview,
  };
};

const dedupeDescriptors = (values: unknown[]): ArtifactDisplayDescriptor[] => {
  const descriptors: ArtifactDisplayDescriptor[] = [];

  values.forEach((value) => {
    const descriptor = normalizeDescriptor(value);

    if (!descriptor) return;
    const existingIndex = descriptors.findIndex(({ tab }) => tab === descriptor.tab);

    if (existingIndex < 0) descriptors.push(descriptor);
    else descriptors[existingIndex] = descriptor;
  });

  return descriptors;
};

const normalizeStoredState = (value: unknown): ArtifactDisplayState => {
  if (!isRecord(value) || value.version !== STORAGE_VERSION || !Array.isArray(value.tabs)) {
    return EMPTY_DISPLAY_STATE;
  }
  const tabs = dedupeDescriptors(value.tabs);

  if (tabs.length === 0) return EMPTY_DISPLAY_STATE;
  const savedActiveTab = isArtifactDisplayTab(value.activeTab) ? value.activeTab : null;
  const activeTab = tabs.some(({ tab }) => tab === savedActiveTab) ? savedActiveTab : tabs[0].tab;

  return { tabs, activeTab, isOpen: value.isOpen === true };
};

export const artifactDisplayStorageKey = (projectId: string): string => (
  `${STORAGE_PREFIX}:${encodeURIComponent(projectId)}`
);

const loadStoredState = (projectId: string | null): ArtifactDisplayState => {
  if (!projectId || typeof window === 'undefined') return EMPTY_DISPLAY_STATE;

  try {
    const raw = window.localStorage.getItem(artifactDisplayStorageKey(projectId));

    return raw ? normalizeStoredState(JSON.parse(raw) as unknown) : EMPTY_DISPLAY_STATE;
  } catch {
    return EMPTY_DISPLAY_STATE;
  }
};

const persistState = (projectId: string | null, state: ArtifactDisplayState): void => {
  if (!projectId || typeof window === 'undefined') return;
  const stored: StoredArtifactDisplayState = { version: STORAGE_VERSION, ...state };

  try {
    window.localStorage.setItem(artifactDisplayStorageKey(projectId), JSON.stringify(stored));
  } catch {
    // Storage can be unavailable (for example, in private browsing); in-memory state remains usable.
  }
};

const descriptorFromRequest = (
  request: OpenArtifactDisplayRequest,
  existing?: ArtifactDisplayDescriptor,
): ArtifactDisplayDescriptor => ({
  tab: request.tab,
  itemId: request.itemId === undefined ? existing?.itemId ?? null : normalizeText(request.itemId),
  subview: request.subview === undefined ? existing?.subview ?? null : normalizeText(request.subview),
});

const descriptorsMatch = (
  left: ArtifactDisplayDescriptor,
  right: ArtifactDisplayDescriptor,
): boolean => (
  left.tab === right.tab
  && left.itemId === right.itemId
  && left.subview === right.subview
);

const updateOpenDisplay = (
  state: ArtifactDisplayState,
  request: OpenArtifactDisplayRequest,
  index: number,
  descriptor: ArtifactDisplayDescriptor,
): ArtifactDisplayState => {
  const descriptorUnchanged = descriptorsMatch(state.tabs[index], descriptor);

  if (descriptorUnchanged && (
    request.source === 'generated'
    || (state.activeTab === request.tab && state.isOpen)
  )) return state;
  const tabs = state.tabs.map((current, currentIndex) => currentIndex === index ? descriptor : current);

  return request.source === 'generated'
    ? { ...state, tabs }
    : { tabs, activeTab: request.tab, isOpen: true };
};

const openDisplay = (
  state: ArtifactDisplayState,
  request: OpenArtifactDisplayRequest,
): ArtifactDisplayState => {
  const index = state.tabs.findIndex(({ tab }) => tab === request.tab);

  // A completed item is a silent follow-up to a phase-start request. It may
  // update a tab that already exists, but it never creates/reopens a workspace
  // on its own. `stream` is the visible phase transition and may do both.
  if (request.source === 'generated' && !state.isOpen && index < 0) return state;
  const descriptor = descriptorFromRequest(request, index >= 0 ? state.tabs[index] : undefined);

  if (index < 0) {
    return { tabs: [...state.tabs, descriptor], activeTab: request.tab, isOpen: true };
  }

  return updateOpenDisplay(state, request, index, descriptor);
};

const activateDisplay = (
  state: ArtifactDisplayState,
  tab: ArtifactDisplayTab,
): ArtifactDisplayState => state.tabs.some((entry) => entry.tab === tab)
  ? { ...state, activeTab: tab, isOpen: true }
  : state;

const closeDisplayTab = (
  state: ArtifactDisplayState,
  tab: ArtifactDisplayTab,
): ArtifactDisplayState => {
  const index = state.tabs.findIndex((entry) => entry.tab === tab);

  if (index < 0) return state;
  const tabs = state.tabs.filter((entry) => entry.tab !== tab);

  if (tabs.length === 0) return EMPTY_DISPLAY_STATE;
  if (state.activeTab !== tab) return { ...state, tabs };
  const activeTab = tabs[index]?.tab ?? tabs[index - 1].tab;

  return { tabs, activeTab, isOpen: state.isOpen };
};

const updateDisplaySelection = (
  state: ArtifactDisplayState,
  tab: ArtifactDisplayTab,
  update: ArtifactSelectionUpdate,
): ArtifactDisplayState => {
  if (!state.tabs.some((entry) => entry.tab === tab)) return state;
  let changed = false;
  const tabs = state.tabs.map((entry) => {
    if (entry.tab !== tab) return entry;
    const itemId = update.itemId === undefined ? entry.itemId : normalizeText(update.itemId);
    const subview = update.subview === undefined ? entry.subview : normalizeText(update.subview);

    if (itemId === entry.itemId && subview === entry.subview) return entry;
    changed = true;

    return { ...entry, itemId, subview };
  });

  return changed ? { ...state, tabs } : state;
};

const displayReducer = (state: ArtifactDisplayState, action: DisplayAction): ArtifactDisplayState => {
  switch (action.type) {
  case 'open': return openDisplay(state, action.request);
  case 'activate': return activateDisplay(state, action.tab);
  case 'close-tab': return closeDisplayTab(state, action.tab);
  case 'close-panel': return { ...state, isOpen: false };
  case 'update-selection': return updateDisplaySelection(state, action.tab, action.update);
  default: return state;
  }
};

const projectReducer = (state: ProjectDisplayState, action: ProjectAction): ProjectDisplayState => {
  if (action.type === 'load-project') {
    return { projectId: action.projectId, display: action.display };
  }
  const display = displayReducer(state.display, action);

  return display === state.display ? state : { ...state, display };
};

const ArtifactDisplayContext = createContext<ArtifactDisplayContextValue | null>(null);

const redirectIOSArtifactRequest = (request: OpenArtifactDisplayRequest): void => {
  if (request.tab !== 'design') {
    window.dispatchEvent(new window.CustomEvent('ios_app_store_restriction'));
  } else {
    // iOS keeps design inside the approved conversation surface. Still emit
    // the canonical event so a hidden AI Overview is brought into view; the
    // provider rejects the design tab itself during request normalization.
    window.dispatchEvent(new window.CustomEvent(OPEN_ARTIFACT_DISPLAY_EVENT, { detail: request }));
    window.dispatchEvent(new window.CustomEvent('scroll_to_artifact', {
      detail: { selector: '[data-artifact="design"]', label: 'Design', tabKey: 'design' },
    }));
  }
};

export const openArtifactDisplay = (request: OpenArtifactDisplayRequest): void => {
  if (typeof window === 'undefined') return;
  if (['design', 'env', 'preview', 'payments'].includes(request.tab) && isIOSApp()) {
    redirectIOSArtifactRequest(request);

    return;
  }
  window.dispatchEvent(new window.CustomEvent(OPEN_ARTIFACT_DISPLAY_EVENT, { detail: request }));
};

type DisplayActions = Omit<ArtifactDisplayContextValue, 'state'>;

/** Stable public actions over the display reducer (user dismissals are announced for stream followers). */
const useDisplayActions = (dispatch: React.Dispatch<DisplayAction>): DisplayActions => {
  const openArtifact = useCallback((request: OpenArtifactDisplayRequest) => {
    const normalized = normalizeOpenRequest(request);

    if (normalized) dispatch({ type: 'open', request: normalized });
  }, [dispatch]);
  const activateTab = useCallback((tab: ArtifactDisplayTab) => dispatch({ type: 'activate', tab }), [dispatch]);
  const closeTab = useCallback((tab: ArtifactDisplayTab) => {
    dispatch({ type: 'close-tab', tab });
    announceDismissed(tab);
  }, [dispatch]);
  const closePanel = useCallback(() => {
    dispatch({ type: 'close-panel' });
    announceDismissed(null);
  }, [dispatch]);
  const updateSelection = useCallback((tab: ArtifactDisplayTab, update: ArtifactSelectionUpdate) => {
    dispatch({ type: 'update-selection', tab, update });
  }, [dispatch]);

  return useMemo(
    () => ({ openArtifact, activateTab, closeTab, closePanel, updateSelection }),
    [activateTab, closePanel, closeTab, openArtifact, updateSelection],
  );
};

// State synchronization, event subscription, and the public action facade meet in this provider.
// eslint-disable-next-line max-statements
export const ArtifactDisplayProvider: React.FC<ArtifactDisplayProviderProps> = ({
  projectId,
  children,
}) => {
  const [projectState, dispatch] = useReducer(projectReducer, projectId, (initialProjectId) => ({
    projectId: initialProjectId,
    display: loadStoredState(initialProjectId),
  }));
  const display = projectState.projectId === projectId ? projectState.display : EMPTY_DISPLAY_STATE;

  useEffect(() => {
    if (projectState.projectId === projectId) return;
    dispatch({ type: 'load-project', projectId, display: loadStoredState(projectId) });
  }, [projectId, projectState.projectId]);

  useEffect(() => {
    if (projectState.projectId === projectId) persistState(projectId, projectState.display);
  }, [projectId, projectState]);

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const request = normalizeOpenRequest((event as ArtifactDisplayEvent).detail);

      if (request) dispatch({ type: 'open', request });
    };

    window.addEventListener(OPEN_ARTIFACT_DISPLAY_EVENT, handleOpen);

    return () => window.removeEventListener(OPEN_ARTIFACT_DISPLAY_EVENT, handleOpen);
  }, []);

  const actions = useDisplayActions(dispatch);
  const value = useMemo<ArtifactDisplayContextValue>(() => ({ state: display, ...actions }), [actions, display]);

  return <ArtifactDisplayContext.Provider value={value}>{children}</ArtifactDisplayContext.Provider>;
};

export const useArtifactDisplay = (): ArtifactDisplayContextValue => {
  const context = useContext(ArtifactDisplayContext);

  if (!context) throw new Error('useArtifactDisplay must be used within ArtifactDisplayProvider');

  return context;
};
